import { useEffect, useState } from "react";
import { http } from "../lib/api";
import { toast } from "sonner";
import { useBranch } from "../context/BranchContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

// Shared "Koreksi Kirim Modal" dialog. Loads the transfer by id, lets a super
// admin fix cabang / valas / nominal / catatan, then PUT /transfers/{id}.
export function TransferCorrectDialog({ transferId, open, onOpenChange, onSaved }) {
  const { branches } = useBranch();
  const [form, setForm] = useState(null);
  const [purpose, setPurpose] = useState("general");
  const [currencies, setCurrencies] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !transferId) return;
    setForm(null);
    http.get("/currencies?active_only=true").then((r) => setCurrencies(r.data || [])).catch(() => {});
    http.get(`/transfers/${transferId}`).then(({ data }) => {
      setForm({ from_branch: data.from_branch_id, to_branch: data.to_branch_id, code: data.code, amount: String(data.amount), note: data.note || "" });
      setPurpose(data.purpose || "general");
    }).catch((e) => { toast.error(e.response?.data?.detail || "Transfer tidak ditemukan"); onOpenChange(false); });
  }, [open, transferId, onOpenChange]);

  const save = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Jumlah harus lebih dari 0");
    if (form.from_branch === form.to_branch) return toast.error("Cabang asal & tujuan harus berbeda");
    setBusy(true);
    try {
      await http.put(`/transfers/${transferId}`, { ...form, amount: amt, purpose });
      toast.success("Kirim modal dikoreksi — saldo & jurnal kedua cabang disesuaikan");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengoreksi"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="transfer-correct-dialog">
        <DialogHeader>
          <DialogTitle>Koreksi Kirim Modal ke Cabang</DialogTitle>
          <DialogDescription>Efek lama dibatalkan lalu nilai baru diterapkan — saldo & jurnal kedua cabang otomatis disesuaikan.</DialogDescription>
        </DialogHeader>
        {form ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-slate-500 mb-1">Dari Cabang</div>
                <Select value={form.from_branch} onValueChange={(v) => setForm({ ...form, from_branch: v })}>
                  <SelectTrigger data-testid="tc-from"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Ke Cabang</div>
                <Select value={form.to_branch} onValueChange={(v) => setForm({ ...form, to_branch: v })}>
                  <SelectTrigger data-testid="tc-to"><SelectValue placeholder="Pilih" /></SelectTrigger>
                  <SelectContent>{branches.filter((b) => b.id !== form.from_branch).map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-slate-500 mb-1">Mata Uang</div>
                <Select value={form.code} onValueChange={(v) => setForm({ ...form, code: v })}>
                  <SelectTrigger data-testid="tc-code"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="IDR">IDR</SelectItem>
                    {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Jumlah</div>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="tc-amount" className="font-mono-num" />
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Catatan (opsional)</div>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="tc-note" />
            </div>
          </div>
        ) : (
          <div className="py-6 text-center text-sm text-slate-400">Memuat…</div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Batal</Button>
          <Button onClick={save} disabled={busy || !form} data-testid="tc-save">{busy ? "Menyimpan…" : "Simpan Koreksi"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Confirm + DELETE helper usable anywhere.
export async function cancelTransferById(transferId, label, onDone) {
  if (!window.confirm(`Batalkan kirim modal ${label || ""}?\nUang dikembalikan ke cabang asal & jurnal antar-cabang dihapus.`)) return;
  try {
    await http.delete(`/transfers/${transferId}`);
    toast.success("Kirim modal dibatalkan — uang dikembalikan ke cabang asal");
    onDone && onDone();
  } catch (e) { toast.error(e.response?.data?.detail || "Gagal membatalkan"); }
}
